import React, { FC, MouseEvent, ReactNode } from 'react';
import { createPictureFactory, Picture } from '@funtech-int/yango-shared';
import { Episode } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { RouterLocation } from '@funtech-int/rival';
import { useIntl } from 'react-intl';
import classnames from 'classnames';

import * as CardKit from '@/shared/components/CardKit';
import { getTruncatedText } from '@/shared/lib/text';
import { formatDuration } from '@/shared/lib/formatTime';
import { useAvailabilityText } from '@/shared/hooks/useAvailabilityText';

import { useI18n } from '../../hooks/useI18n';

import styles from './_EpisodeSnippet.module.css';

const MAX_DESCRIPTION_LENGTH = 160;

const pictureFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '160x90',
            '2x': '375x234', // actually 1.7x but this is maximum available size
        },
        src: '375x234',
    },
});

interface EpisodeSnippetProps {
    episode: Episode;
    location: RouterLocation<{ id: string }, unknown>;
    currentEpisodeWatchingProgress: number | undefined;
    isSelected: boolean;
    onClick: (event: MouseEvent<HTMLDivElement>) => void;
}

export const EpisodeSnippet: FC<EpisodeSnippetProps> = ({
    episode,
    location,
    onClick,
    isSelected,
    currentEpisodeWatchingProgress,
}) => {
    const intl = useIntl();
    const i18n = useI18n();

    const title = episode.title.toString()
        ? `${episode.number}. ${episode.title.toString()}`
        : i18n('episode_number', { episodeNumber: episode.number });

    const description = getTruncatedText(episode.synopsis || '', MAX_DESCRIPTION_LENGTH);
    const { img } = pictureFactory({ fallbackUrl: episode.cover?.toString() });
    let releaseStatusOverlay: ReactNode;
    let progress: ReactNode | undefined;
    const isWatchable = Boolean(episode.viewOption?.isWatchable);

    const watchProgressPosition = currentEpisodeWatchingProgress ?? episode.watchProgressPosition;

    const watchingStarted = Boolean(watchProgressPosition);

    const isLocked = !episode.availabilityStatus || episode.isDisabled;
    const isEnabled = !isLocked && (!episode.isPublished || isWatchable);

    if (episode.isPublished && isWatchable) {
        const formattedDuration = formatDuration(intl, episode.duration);

        progress = (
            <CardKit.Progress
                value={watchProgressPosition ?? 0}
                maxValue={episode.duration}
                dateTime={formattedDuration}
                text={formattedDuration}
            />
        );
    }

    if (!isLocked && !episode.isPublished) {
        const releaseDate = episode.plannedAvailabilityDate
            ? new Date(Date.parse(episode.plannedAvailabilityDate))
            : null;

        releaseStatusOverlay = <CardKit.ReleaseDate releaseDate={releaseDate} />;
    }

    const expirationDateMs = episode.availabilityEndDate
        ? Date.parse(episode.availabilityEndDate)
        : null;

    const availabilityText = useAvailabilityText(expirationDateMs, episode.availabilityStatus);

    const showNote = Boolean(episode.editorAnnotation && isEnabled && !availabilityText);
    const showDot = !watchingStarted && isEnabled;
    const subtitleLines = availabilityText || showNote ? 3 : 4;

    return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
            className={classnames(
                styles.episodeSnippet,
                { [styles.episodeSnippetDisabled]: isLocked || releaseStatusOverlay },
                { [styles.episodeSnippetSelected]: isSelected }
            )}
            onClick={onClick}
        >
            <CardKit.Layout location={location} className={styles.episodeSnippetLayout}>
                <CardKit.ImageOverlay
                    image={
                        <Picture
                            alt=""
                            className={styles.image}
                            data-tid="AdaptiveImage"
                            loading={undefined}
                            src={img.src}
                            srcSet={img.srcSet}
                        />
                    }
                >
                    {isLocked && <CardKit.Lock />}
                    {releaseStatusOverlay}
                    {progress}
                </CardKit.ImageOverlay>
            </CardKit.Layout>
            <CardKit.Caption
                dot={showDot}
                subtitle={isEnabled ? description : undefined}
                subtitleLines={subtitleLines}
                title={title}
            >
                {availabilityText && (
                    <CardKit.ExpirationLabel variant={isEnabled ? 'warning' : 'default'}>
                        {availabilityText}
                    </CardKit.ExpirationLabel>
                )}
                {showNote && <CardKit.Note lines={5}>{episode.editorAnnotation}</CardKit.Note>}
            </CardKit.Caption>
        </div>
    );
};
